import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_HEADERS = {
  salgados: "Salgados · 20g · fritos",
  brigadeiros: "Brigadeiros artesanais",
  personalizados: "Brigadeiros personalizados · 16g · massa Nido",
  kits: "Kit pronto Caseirinhos",
};

const PREMIUM_FLAVORS = new Set(["Nido com Nutella", "Ferrero Rocher"]);
const SURCHARGE = 0.15;
const NO_FLAVOR_LIMIT = 99;

function computeSurcharge(product, variant, selected, qty) {
  if (!product || !variant || !selected) return 0;
  const flavors = selected.brigadeiros || [];
  if (!flavors.length) return 0;
  const premium = flavors.filter((f) => PREMIUM_FLAVORS.has(f)).length;
  if (!premium) return 0;
  let brigUnits = variant.pack_size;
  if (product.category === "kits") {
    const m = (variant.label || "").match(/(\d+)\s+brigadeiros/);
    if (m) brigUnits = parseInt(m[1], 10);
  }
  const unitsPerFlavor = brigUnits / flavors.length;
  return Math.round(unitsPerFlavor * premium * SURCHARGE * qty * 100) / 100;
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [customization, setCustomization] = useState({ theme: "", colors: "" });
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setVariantId(r.data.variants[0].id);
      const init = {};
      (r.data.flavor_groups || []).forEach((g) => { init[g.key] = []; });
      setSelected(init);
    });
  }, [id]);

  const variant = useMemo(() => product?.variants.find((v) => v.id === variantId), [product, variantId]);
  const flavorGroups = product?.flavor_groups || [];
  const surcharge = computeSurcharge(product, variant, selected, qty);
  const baseTotal = (variant?.price || 0) * qty;
  const finalTotal = baseTotal + surcharge;

  // Reset selected flavors when variant changes (limits may differ)
  useEffect(() => {
    if (!variant) return;
    setSelected((prev) => {
      const next = {};
      flavorGroups.forEach((g) => {
        const limit = variant.flavor_limits?.[g.key] ?? NO_FLAVOR_LIMIT;
        next[g.key] = (prev[g.key] || []).slice(0, limit);
      });
      return next;
    });
  }, [variantId, variant, flavorGroups]);

  if (!product) return <div className="py-32 text-center text-cocoa-soft">A carregar...</div>;

  const toggleFlavor = (groupKey, flavor) => {
    const limit = variant.flavor_limits?.[groupKey] ?? NO_FLAVOR_LIMIT;
    setSelected((prev) => {
      const current = prev[groupKey] || [];
      if (current.includes(flavor)) return { ...prev, [groupKey]: current.filter((f) => f !== flavor) };
      if (current.length >= limit) {
        toast.error(`Pode escolher no máximo ${limit} sabores.`);
        return prev;
      }
      return { ...prev, [groupKey]: [...current, flavor] };
    });
  };

  const handleAdd = () => {
    for (const g of flavorGroups) {
      const limit = variant.flavor_limits?.[g.key] ?? NO_FLAVOR_LIMIT;
      const picked = (selected[g.key] || []).length;
      if (picked < 1) {
        toast.error(`Escolha pelo menos um sabor em "${g.label}".`);
        return;
      }
      if (picked > limit) {
        toast.error(`Máximo ${limit} sabores em "${g.label}".`);
        return;
      }
    }
    if (product.requires_customization) {
      if (!customization.theme.trim() || !customization.colors.trim()) {
        toast.error("Indique o tema e as cores desejadas.");
        return;
      }
    }
    addItem(product, variant, qty, selected, product.requires_customization ? customization : null);
    toast.success("Adicionado ao cesto");
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
      <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm uppercase tracking-widest text-cocoa-soft hover:text-rose-accent">
        <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
      </Link>
      <div className="mt-8 grid lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-3xl overflow-hidden bg-cream-alt lg:sticky lg:top-28 self-start">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-rose-accent">
            {CATEGORY_HEADERS[product.category] || ""}
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl text-cocoa-dark mt-3 leading-tight">{product.name}</h1>
          <p className="text-cocoa-soft text-lg mt-5 leading-relaxed">{product.description}</p>

          {product.variants.length > 1 && (
            <div className="mt-10">
              <p className="text-xs uppercase tracking-widest text-cocoa-soft mb-3">Escolha o tamanho</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`detail-variant-${v.id}`}
                    onClick={() => setVariantId(v.id)}
                    className={`text-left p-3 rounded-2xl border-2 transition-colors ${
                      variantId === v.id ? "border-rose bg-rose/10" : "border-cocoa/10 hover:border-rose/50"
                    }`}
                  >
                    <div className="text-sm font-medium text-cocoa-dark">{v.label}</div>
                    <div className="font-heading text-2xl text-cocoa-dark mt-1">€{v.price.toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {flavorGroups.map((group) => {
            const limit = variant.flavor_limits?.[group.key] ?? NO_FLAVOR_LIMIT;
            const picked = selected[group.key] || [];
            return (
              <div key={group.key} className="mt-10" data-testid={`flavor-group-${group.key}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-xs uppercase tracking-widest text-cocoa-soft">{group.label}</p>
                  <span className={`text-sm font-medium ${picked.length === limit ? "text-mint" : "text-rose-accent"}`}>
                    {picked.length} / {limit} selecionados
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.options.map((opt) => {
                    const active = picked.includes(opt);
                    const full = !active && picked.length >= limit;
                    const isPremium = PREMIUM_FLAVORS.has(opt);
                    return (
                      <button
                        key={opt}
                        data-testid={`flavor-${group.key}-${opt}`}
                        onClick={() => toggleFlavor(group.key, opt)}
                        disabled={full}
                        className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border-2 transition-all ${
                          active
                            ? "bg-cocoa-dark text-cream border-cocoa-dark"
                            : full
                              ? "bg-cream-alt text-cocoa-soft/40 border-cocoa/10 cursor-not-allowed"
                              : "bg-cream text-cocoa-dark border-cocoa/15 hover:border-rose hover:bg-rose/10"
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5" />}
                        {opt}
                        {isPremium && (
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${active ? "bg-rose text-cocoa-dark" : "bg-rose/20 text-rose-accent"}`}>
                            +€0,15/un
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {product.requires_customization && (
            <div data-testid="examples-gallery" className="mt-10">
              <div className="flex items-baseline justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-rose-accent">Inspiração · trabalhos anteriores</p>
                <span className="text-xs text-cocoa-soft">10 temas</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {[
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/crmdjzfl_IMG_1417.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/8w5f6tqz_IMG_1418.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/dvt0gt8g_IMG_1419.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/3866zmnc_IMG_1420.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/52qiw0jm_IMG_1421.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/bxv99lxc_IMG_1422.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/5edh25ur_IMG_1423.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/t7noqd0p_IMG_1424.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/r4xei0l7_IMG_1425.jpeg",
                  "https://customer-assets.emergentagent.com/job_brigadeiro-shop-10/artifacts/xkp07akl_IMG_1426.jpeg",
                ].map((src, i) => (
                  <div key={src} className="aspect-square rounded-xl overflow-hidden bg-cream-alt lift">
                    <img src={src} alt={`Exemplo personalizado ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-cocoa-soft mt-2 italic">Cada encomenda é única — inspire-se nos nossos trabalhos anteriores (personagens, temas festivos, casamentos, batizados).</p>
            </div>
          )}

          {product.requires_customization && (
            <div data-testid="customization-block" className="mt-8 bg-rose/10 border border-rose/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-rose-accent" />
                <p className="text-sm uppercase tracking-widest text-rose-accent font-medium">Personalize a sua encomenda</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-cocoa-soft mb-1.5 block">Tema *</label>
                  <input
                    data-testid="custom-theme"
                    value={customization.theme}
                    onChange={(e) => setCustomization({ ...customization, theme: e.target.value })}
                    placeholder="Ex: Batizado, Hello Kitty, Casamento, Princesas..."
                    className="w-full bg-cream border border-cocoa/15 rounded-xl px-4 py-3 text-cocoa-dark placeholder:text-cocoa-soft/60 focus:outline-none focus:ring-2 focus:ring-rose"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-cocoa-soft mb-1.5 block">Cores desejadas *</label>
                  <input
                    data-testid="custom-colors"
                    value={customization.colors}
                    onChange={(e) => setCustomization({ ...customization, colors: e.target.value })}
                    placeholder="Ex: rosa e dourado, azul bebé e branco..."
                    className="w-full bg-cream border border-cocoa/15 rounded-xl px-4 py-3 text-cocoa-dark placeholder:text-cocoa-soft/60 focus:outline-none focus:ring-2 focus:ring-rose"
                  />
                </div>
              </div>
            </div>
          )}

          {product.requires_customization && (
            <a
              data-testid="whatsapp-chat-button"
              href={`https://wa.me/351920248910?text=${encodeURIComponent("Olá Caseirinhos! Gostaria de conversar sobre uma encomenda de brigadeiros personalizados.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-mint hover:bg-mint/80 text-cocoa-dark px-5 py-3 rounded-full font-medium transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Conversar no WhatsApp
            </a>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <div className="flex items-center border-2 border-cocoa/15 rounded-full overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:bg-cream-alt">
                <Minus className="w-4 h-4 text-cocoa-soft" />
              </button>
              <span data-testid="detail-qty" className="px-5 text-cocoa-dark font-medium">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:bg-cream-alt">
                <Plus className="w-4 h-4 text-cocoa-soft" />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={handleAdd}
              className="flex-1 sm:flex-none inline-flex items-center gap-2 justify-center bg-cocoa-dark hover:bg-cocoa text-cream px-8 py-4 rounded-full font-medium transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              Adicionar — €{finalTotal.toFixed(2)}
            </button>
          </div>
          {surcharge > 0 && (
            <p data-testid="surcharge-note" className="text-xs text-rose-accent mt-3">
              Inclui acréscimo de €{surcharge.toFixed(2)} pelos sabores premium escolhidos.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
